import { useEffect, useState } from "react";
import SectionHeader from "@/components/SectionHeader";
import { 
  Heart, 
  MessageCircle, 
  Eye, 
  Send, 
  Upload as UploadIcon, 
  X, 
  MapPin, 
  Users, 
  User, 
  Maximize2, 
  ChevronLeft, 
  ChevronRight,
  Layers
} from "lucide-react";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { sfx } from "@/lib/sounds";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["All", "Spawn", "Medieval", "Mega Base", "Arena", "Cyber", "Village", "Redstone", "Memory", "Other"];

function BuildTile({ b, onOpen, onLike }) {
  const navigate = useNavigate();
  const [activeImgIdx, setActiveImgIdx] = useState(0);

  const images = b.images && b.images.length > 0 ? b.images : (b.image ? [b.image] : []);
  const currentSrc = images[activeImgIdx] || images[0];

  const goAuthor = (e) => {
    e.stopPropagation();
    if (b.author_id) { 
      sfx.click(); 
      navigate(`/users/${b.author_id}`); 
    }
  };

  return (
    <div 
      data-testid={`build-tile-${b.id}`} 
      className="group relative rounded-xl overflow-hidden bg-black/60 border border-white/10 hover:border-[#FFBF00]/60 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-[0_10px_25px_rgba(255,191,0,0.2)] flex flex-col cursor-pointer" 
      onClick={() => { sfx.click(); onOpen(b.id); }}
    >
      {/* Rectangular 16:9 Aspect Ratio Container */}
      <div className="relative w-full aspect-video overflow-hidden bg-zinc-950">
        {currentSrc ? (
          <img 
            src={currentSrc} 
            alt={b.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" 
            loading="lazy" 
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs font-mono">No Image</div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />

        {/* Multi-image count badge */}
        {images.length > 1 && (
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/70 border border-white/10 text-[10px] font-mono text-white/90 backdrop-blur-md z-10">
            <Layers size={11} className="text-[#FFBF00]" />
            <span>{activeImgIdx + 1}/{images.length}</span>
          </div>
        )}

        {/* Author chip in top-right */}
        {b.author_id && (
          <button
            onClick={goAuthor}
            data-testid={`build-author-chip-${b.id}`}
            className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 border border-white/10 hover:border-[#FFBF00]/60 text-[10px] uppercase tracking-wider backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity z-10"
            title={`Open ${b.author_name || b.author}'s profile`}
          >
            {b.author_avatar ? (
              <img src={b.author_avatar} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
            ) : (
              <User size={10} />
            )}
            <span className="max-w-[80px] truncate">{b.author_name || b.author}</span>
          </button>
        )}

        {/* Multiple Images Hover Thumbnail Selector */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-2 right-2 flex gap-1 overflow-x-auto py-1 opacity-0 group-hover:opacity-100 transition-opacity z-10" onClick={(e) => e.stopPropagation()}>
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImgIdx(i)}
                className={`w-8 h-6 rounded overflow-hidden border transition-all flex-shrink-0 ${
                  i === activeImgIdx ? "border-[#FFBF00] scale-105" : "border-black/60 opacity-60 hover:opacity-100"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Card Metadata Footer */}
      <div className="p-3 bg-black/80 border-t border-white/5 flex flex-col justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#FFBF00] font-semibold">{b.category}</div>
          <div className="font-hero text-lg leading-snug truncate text-white mt-0.5">{b.title}</div>
          <button onClick={goAuthor} className="text-[11px] text-white/60 hover:text-[#FFBF00] transition-colors block truncate">
            by {b.author_name || b.author}
          </button>
        </div>

        <div className="flex items-center gap-3.5 mt-2.5 pt-2 border-t border-white/10 text-[11px] text-white/70">
          <button
            onClick={(e) => { e.stopPropagation(); onLike(b.id); }}
            data-testid={`build-like-${b.id}`}
            className={`flex items-center gap-1 hover:text-[#FFBF00] transition-colors ${b.liked ? "text-[#FFBF00]" : ""}`}
          >
            <Heart size={13} fill={b.liked ? "currentColor" : "none"} /> {b.likes_count ?? 0}
          </button>
          <span className="flex items-center gap-1"><MessageCircle size={13} /> {b.comments_count ?? 0}</span>
          <span className="flex items-center gap-1"><Eye size={13} /> {b.views ?? 0}</span>
        </div>
      </div>
    </div>
  );
}

function BuildModal({ id, onClose, onRefresh }) {
  const { user } = useAuth();
  const [build, setBuild] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [imgIdx, setImgIdx] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setImgIdx(0);
    setIsFullScreen(false);
    authClient.get(`/builds/${id}`).then((r) => setBuild(r.data));
    authClient.get(`/builds/${id}/comments`).then((r) => setComments(r.data));
  }, [id]);

  const like = async () => {
    if (!user) return toast.error("Sign in to like builds");
    const { data } = await authClient.post(`/builds/${id}/like`);
    sfx.pling();
    setBuild({ ...build, liked: data.liked, likes_count: data.count });
    onRefresh?.();
  };

  const sendComment = async () => {
    if (!user) return toast.error("Sign in to comment");
    if (!comment.trim()) return;
    try {
      const { data } = await authClient.post(`/builds/${id}/comments`, { body: comment });
      setComments([...comments, data]);
      setComment("");
      sfx.success();
    } catch (e) { 
      sfx.error(); 
      toast.error(formatApiError(e)); 
    }
  };

  if (!build) return null;

  const images = build.images && build.images.length > 0 ? build.images : (build.image ? [build.image] : []);
  const src = images[imgIdx] || images[0];

  const prevImage = (e) => {
    e?.stopPropagation();
    setImgIdx((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const nextImage = (e) => {
    e?.stopPropagation();
    setImgIdx((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <>
      {/* Standard Foreground Detail Modal */}
      <Dialog open={!!id && !isFullScreen} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-black/95 border border-white/10 text-white max-w-5xl p-0 overflow-hidden rounded-2xl shadow-2xl z-[100]" data-testid="build-modal">
          <DialogHeader className="sr-only">
            <DialogTitle>{build.title}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-12 min-h-[480px]">
            {/* Left Column: Rectangular Image Display */}
            <div className="md:col-span-7 bg-zinc-950 flex flex-col justify-between relative">
              <div className="relative flex-1 flex items-center justify-center p-4 min-h-[300px] md:min-h-[450px]">
                {src && (
                  <img 
                    src={src} 
                    alt={build.title} 
                    className="w-full max-h-[60vh] object-contain rounded-lg cursor-zoom-in" 
                    onClick={() => setIsFullScreen(true)}
                  />
                )}

                {/* Full Screen Trigger Button */}
                <button 
                  onClick={() => setIsFullScreen(true)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 text-white transition-all backdrop-blur-md opacity-90 hover:opacity-100 z-10"
                  title="View Full Screen"
                >
                  <Maximize2 size={16} />
                </button>

                {/* Image Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button 
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/70 hover:bg-black/90 border border-white/10 text-white transition-all backdrop-blur-md z-10"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button 
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/70 hover:bg-black/90 border border-white/10 text-white transition-all backdrop-blur-md z-10"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>

              {/* Multiple Images Selector Strip */}
              {images.length > 1 && (
                <div className="flex gap-2 p-3 bg-black/60 border-t border-white/5 justify-center overflow-x-auto">
                  {images.map((im, i) => (
                    <button 
                      key={i} 
                      onClick={() => setImgIdx(i)} 
                      className={`w-14 h-10 rounded overflow-hidden border-2 transition-all flex-shrink-0 ${i === imgIdx ? "border-[#FFBF00] scale-105" : "border-white/10 opacity-50 hover:opacity-100"}`}
                    >
                      <img src={im} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Build Information & Comments */}
            <div className="md:col-span-5 p-6 flex flex-col justify-between overflow-y-auto max-h-[85vh] bg-black/40">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] font-semibold">{build.category}</div>
                <div className="font-hero text-2xl mt-1">{build.title}</div>
                
                <div className="flex items-center gap-2 mt-2">
                  {build.author_avatar && <img src={build.author_avatar} alt="" className="w-5 h-5 rounded-full object-cover" />}
                  <span className="text-xs text-white/70">by {build.author_name || build.author}</span>
                </div>

                <p className="text-xs text-white/70 mt-3 leading-relaxed">{build.description}</p>

                <div className="mt-3 space-y-1 text-xs text-white/60 font-mono bg-white/5 p-2.5 rounded-lg border border-white/5">
                  {build.server && <div>SERVER · <span className="text-white/80">{build.server}</span></div>}
                  {build.coordinates && (
                    <div className="flex items-center gap-1.5">
                      <MapPin size={11} className="text-[#FFBF00]" /> {build.coordinates}
                    </div>
                  )}
                  {build.team?.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <Users size={11} className="text-[#FFBF00]" /> {build.team.join(", ")}
                    </div>
                  )}
                </div>

                {build.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {build.tags.map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-white/70 bg-white/5">
                        #{t}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-4 mt-4 text-xs">
                  <button 
                    data-testid="modal-like" 
                    onClick={like} 
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full border transition-all ${
                      build.liked 
                        ? "border-[#FFBF00]/50 text-[#FFBF00] bg-[#FFBF00]/10" 
                        : "border-white/10 text-white/70 hover:text-[#FFBF00] hover:border-[#FFBF00]/30"
                    }`}
                  >
                    <Heart size={14} fill={build.liked ? "currentColor" : "none"} /> {build.likes_count}
                  </button>
                  <span className="flex items-center gap-1.5 text-white/70"><Eye size={14} /> {build.views}</span>
                </div>
              </div>

              {/* Comments Section */}
              <div className="mt-5 border-t border-white/10 pt-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] font-semibold mb-2">
                  Comments · {comments.length}
                </div>
                <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1" data-testid="build-comments">
                  {comments.map((c) => (
                    <div key={c.id} className="flex items-start gap-2">
                      <img src={c.author_avatar} alt="" className="w-6 h-6 rounded-full object-cover mt-0.5" />
                      <div className="flex-1 rounded-lg bg-white/5 p-2 border border-white/5">
                        <div className="text-[11px] font-semibold text-white/90">{c.author_name}</div>
                        <div className="text-xs text-white/70 mt-0.5">{c.body}</div>
                      </div>
                    </div>
                  ))}
                  {comments.length === 0 && <div className="text-xs text-white/40 italic">Be the first to comment!</div>}
                </div>

                {user ? (
                  <div className="flex gap-2 mt-3">
                    <input
                      data-testid="build-comment-input"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Write a comment..."
                      className="flex-1 bg-black/60 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-[#FFBF00] transition-colors"
                      onKeyDown={(e) => e.key === "Enter" && sendComment()}
                    />
                    <button 
                      data-testid="build-comment-send" 
                      onClick={sendComment} 
                      className="px-3 py-1.5 rounded-lg bg-[#FFBF00] hover:bg-[#FF8C00] text-black transition-colors"
                    >
                      <Send size={13} />
                    </button>
                  </div>
                ) : (
                  <div className="text-[11px] text-white/50 mt-2 italic">Sign in to comment.</div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-Screen Foreground Overlay */}
      {isFullScreen && (
        <div 
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-2xl flex flex-col justify-between p-4 sm:p-6"
          onClick={() => setIsFullScreen(false)}
        >
          {/* Lightbox Header */}
          <div className="flex items-center justify-between text-white z-10">
            <div>
              <h3 className="font-hero text-xl">{build.title}</h3>
              <p className="text-xs text-white/60">Image {imgIdx + 1} of {images.length}</p>
            </div>
            <button 
              onClick={() => setIsFullScreen(false)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Full Screen Image */}
          <div className="relative flex-1 flex items-center justify-center my-2 overflow-hidden">
            <img 
              src={src} 
              alt={build.title} 
              className="max-w-full max-h-[85vh] object-contain drop-shadow-2xl" 
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navigation Controls */}
            {images.length > 1 && (
              <>
                <button 
                  onClick={prevImage}
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 text-white transition-all backdrop-blur-md"
                >
                  <ChevronLeft size={28} />
                </button>
                <button 
                  onClick={nextImage}
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 text-white transition-all backdrop-blur-md"
                >
                  <ChevronRight size={28} />
                </button>
              </>
            )}
          </div>

          {/* Bottom Thumbnails Strip */}
          {images.length > 1 && (
            <div className="flex gap-2 justify-center overflow-x-auto py-2 z-10" onClick={(e) => e.stopPropagation()}>
              {images.map((im, i) => (
                <button 
                  key={i} 
                  onClick={() => setImgIdx(i)} 
                  className={`w-14 h-10 rounded overflow-hidden border-2 transition-all flex-shrink-0 ${i === imgIdx ? "border-[#FFBF00] scale-110" : "border-white/20 opacity-50 hover:opacity-100"}`}
                >
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function FeaturedBuildShowcase({ featured, onOpen }) {
  const hero = featured?.day || featured?.week || featured?.month;

  if (!hero) {
    return (
      <div className="mb-10">
        <div className="relative aspect-video w-full overflow-hidden rounded-3xl border border-white/10 bg-black/40 p-8 sm:p-12 text-center flex items-center justify-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,191,0,0.08),transparent_55%)]" />

          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#FFBF00] mb-3">
              Featured Builds
            </div>

            <h2 className="font-hero text-2xl sm:text-4xl text-white">
              The showcase is loading
            </h2>

            <p className="text-sm text-white/45 mt-2">
              Featured community builds will appear here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const periodLabel = (item) => {
    const period = item?.featured?.period;

    if (period === "day") return "Build of the Day";
    if (period === "week") return "Build of the Week";
    if (period === "month") return "Build of the Month";

    return "Featured Build";
  };

  const imageFor = (item) => item?.images?.[0] || item?.image || "";

  return (
    <section className="mb-12">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#FFBF00] font-semibold">
            LostDC Showcase
          </div>

          <h2 className="font-hero text-2xl sm:text-3xl lg:text-4xl text-white mt-1">
            Featured Build
          </h2>
        </div>

        <div className="hidden sm:block text-[10px] uppercase tracking-widest text-white/30">
          Curated by LostDC
        </div>
      </div>

      <div className="w-full">
        <button
          type="button"
          onClick={() => {
            sfx.click();
            onOpen(hero.id);
          }}
          className="group relative w-full aspect-video max-h-[70vh] overflow-hidden rounded-3xl border border-[#FFBF00]/20 bg-black text-left shadow-[0_25px_80px_rgba(0,0,0,0.35)]"
        >
          {imageFor(hero) ? (
            <img
              src={imageFor(hero)}
              alt={hero.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 bg-zinc-900" />
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

          <div className="absolute top-5 left-5 sm:top-7 sm:left-7 flex items-center gap-2 z-10">
            <span className="px-3 py-1.5 rounded-full bg-[#FFBF00] text-black text-[9px] sm:text-xs uppercase tracking-[0.2em] font-bold shadow-lg">
              {periodLabel(hero)}
            </span>

            {hero.featured?.selection_mode === "auto" && (
              <span className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white/80 text-[9px] sm:text-xs uppercase tracking-[0.2em]">
                Auto Selected
              </span>
            )}
          </div>

          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 lg:p-10 z-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#FFBF00] font-semibold">
              {hero.category}
            </div>

            <h3 className="font-hero text-2xl sm:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mt-2 drop-shadow-md">
              {hero.title}
            </h3>

            <div className="flex flex-wrap items-center gap-3 mt-3 sm:mt-4 text-xs sm:text-sm text-white/70">
              <span>
                by {hero.author_name || hero.author || "Unknown"}
              </span>

              <span className="w-1 h-1 rounded-full bg-white/40" />

              <span>{hero.likes_count || 0} likes</span>

              <span className="w-1 h-1 rounded-full bg-white/40" />

              <span>{hero.views || 0} views</span>
            </div>
          </div>
        </button>
      </div>
    </section>
  );
}

export default function Builds() {
  const { user } = useAuth();
  const [builds, setBuilds] = useState([]);
  const [featured, setFeatured] = useState({
    day: null,
    week: null,
    month: null,
  });
  const [filter, setFilter] = useState("All");
  const [openId, setOpenId] = useState(null);

  const load = () =>
    authClient
      .get("/builds")
      .then((r) => setBuilds(r.data))
      .catch(() => {});

  const loadFeatured = () =>
    authClient
      .get("/builds/featured/current")
      .then((r) => setFeatured(r.data))
      .catch(() => {
        setFeatured({
          day: null,
          week: null,
          month: null,
        });
      });

  useEffect(() => {
    load();
    loadFeatured();
  }, []);

  const toggleLike = async (id) => {
    if (!user) return toast.error("Sign in to like builds");
    try {
      const { data } = await authClient.post(`/builds/${id}/like`);
      sfx.pling();
      setBuilds(builds.map(b => b.id === id ? { ...b, liked: data.liked, likes_count: data.count } : b));
    } catch (e) { 
      sfx.error(); 
      toast.error(formatApiError(e)); 
    }
  };

  const filtered = filter === "All" ? builds : builds.filter(b => b.category === filter);

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-builds">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <SectionHeader 
            eyebrow="Gallery" 
            title="Community Builds" 
            subtitle="Curated masterpieces built by the LostDC community over 8 seasons." 
          />
          <Link
            to="/builds/upload"
            data-testid="builds-upload-cta"
            onClick={() => sfx.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold transition-all hover:scale-105 shadow-lg shadow-[#FFBF00]/20"
          >
            <UploadIcon size={14} /> Upload Build
          </Link>
        </div>

        {/* Full-width Widescreen 16:9 Featured Build Showcase */}
        <FeaturedBuildShowcase
          featured={featured}
          onOpen={setOpenId}
          onLike={toggleLike}
        />

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="builds-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => { setFilter(c); sfx.click(); }}
              data-testid={`builds-filter-${c.toLowerCase().replace(/\s/g,'-')}`}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-all ${
                filter === c 
                  ? "bg-[#FFBF00] text-black font-semibold shadow-md shadow-[#FFBF00]/20" 
                  : "bg-white/5 border border-white/10 hover:border-[#FFBF00]/40 text-white/80 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Rectangle Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((b) => (
            <BuildTile key={b.id} b={b} onOpen={setOpenId} onLike={toggleLike} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center text-white/50 py-20 bg-white/5 rounded-xl border border-white/5">
            No builds found in this category yet.
          </div>
        )}
      </div>

      {/* Build Details Modal & Full-screen view */}
      <BuildModal id={openId} onClose={() => setOpenId(null)} onRefresh={load} />
    </div>
  );
}